import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { cn, prefersReducedMotion } from '../../lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'google' | 'github';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  asChild?: boolean;
  children: ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-500 text-text-inverse hover:bg-brand-600 hover:-translate-y-px hover:shadow-elev-2',
  secondary:
    'bg-bg-surface text-text-primary border border-border-default hover:bg-bg-hover hover:border-border-strong',
  ghost: 'bg-transparent text-text-primary hover:bg-bg-hover',
  danger: 'bg-error-400 text-white hover:bg-error-600',
  google: 'bg-bg-surface text-text-primary border border-border-default hover:bg-bg-hover',
  github: 'bg-neutral-800 text-white hover:bg-neutral-900',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs rounded-md gap-1.5',
  md: 'h-10 px-5 text-sm rounded-lg gap-2',
  lg: 'h-12 px-6 text-base rounded-lg gap-2.5',
  icon: 'h-10 w-10 rounded-md gap-0 p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      leadingIcon,
      trailingIcon,
      asChild = false,
      disabled,
      children,
      className,
      type = 'button',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const reducedMotion = prefersReducedMotion();

    const Comp: React.ElementType = asChild ? Slot : motion.button;

    const motionProps: HTMLMotionProps<'button'> = reducedMotion
      ? {}
      : {
          whileHover: isDisabled ? undefined : { y: -1 },
          whileTap: isDisabled ? undefined : { y: 0 },
          transition: { duration: 0.15, ease: [0.16, 1, 0.3, 1] },
        };

    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        disabled={asChild ? undefined : isDisabled}
        aria-busy={loading || undefined}
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap font-medium',
          'transition-[background-color,border-color,transform,box-shadow] duration-fast',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2',
          'disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...(asChild ? {} : motionProps)}
        {...rest}
      >
        {loading ? (
          <span className="inline-flex items-center gap-2">
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
              aria-hidden
            />
            <span>{children}</span>
          </span>
        ) : (
          <>
            {leadingIcon && <span className="inline-flex">{leadingIcon}</span>}
            {children}
            {trailingIcon && <span className="inline-flex">{trailingIcon}</span>}
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';
